import { useState } from "react";
import { EnvelopeSimple, CircleNotch, Check, ChatCircle, Clock } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import PublicNav from "@/components/PublicNav";
import axios from "axios";
import { API } from "@/App";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState(null); // null | 'sending' | 'sent' | 'error'

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.message) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO title="Contact & Support — Dynasty Trade Maker" description="Get in touch with the Dynasty Trade Maker team. Questions, feedback, bug reports, or feature requests — we respond to every message." path="/contact" />
      <PublicNav />
      <div className="max-w-3xl mx-auto px-4 py-12">

        {/* Hero */}
        <div className="text-center mb-10">
          <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#ccff00] block mb-3">SUPPORT</span>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-white uppercase mb-4">
            GET IN <span className="text-[#ccff00]">TOUCH</span>
          </h1>
          <p className="text-base text-[#a3a3a3] max-w-xl mx-auto">
            {"Questions, feedback, bug reports, or feature requests \u2014 we read and respond to every message."}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <div className="border border-[#262626] bg-[#171717] p-5 rounded-sm text-center">
            <ChatCircle size={24} className="text-[#ccff00] mx-auto mb-2" weight="bold" />
            <p className="text-sm font-bold text-white mb-1">General Questions</p>
            <p className="text-xs text-[#888]">How features work, account help, or getting started</p>
          </div>
          <div className="border border-[#262626] bg-[#171717] p-5 rounded-sm text-center">
            <EnvelopeSimple size={24} className="text-[#45b7d1] mx-auto mb-2" weight="bold" />
            <p className="text-sm font-bold text-white mb-1">Bug Reports</p>
            <p className="text-xs text-[#888]">Something broken or not working as expected</p>
          </div>
          <div className="border border-[#262626] bg-[#171717] p-5 rounded-sm text-center">
            <Clock size={24} className="text-[#f9ca24] mx-auto mb-2" weight="bold" />
            <p className="text-sm font-bold text-white mb-1">Feature Requests</p>
            <p className="text-xs text-[#888]">Ideas for new tools or improvements</p>
          </div>
        </div>

        {status === "sent" ? (
          <div className="text-center py-16 border border-[#262626] bg-[#171717] rounded-sm">
            <div className="w-14 h-14 bg-[#ccff00]/10 border border-[#ccff00]/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check size={28} className="text-[#ccff00]" weight="bold" />
            </div>
            <h2 className="font-heading text-2xl text-white uppercase mb-2">MESSAGE SENT</h2>
            <p className="text-sm text-[#a3a3a3] max-w-md mx-auto">{"Thanks for reaching out. We\u2019ll get back to you as soon as possible \u2014 usually within a few hours."}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="border border-[#262626] bg-[#171717] rounded-sm p-6 sm:p-8 space-y-5" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-[#555] block mb-1.5">Name</label>
                <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Your name" className="w-full h-11 px-4 bg-[#0a0a0a] border border-[#262626] text-white text-sm rounded-sm placeholder:text-[#555] focus:border-[#ccff00] focus:outline-none transition-colors" data-testid="contact-name" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-[#555] block mb-1.5">Email <span className="text-[#ccff00]">*</span></label>
                <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="your@email.com" required className="w-full h-11 px-4 bg-[#0a0a0a] border border-[#262626] text-white text-sm rounded-sm placeholder:text-[#555] focus:border-[#ccff00] focus:outline-none transition-colors" data-testid="contact-email" />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-[#555] block mb-1.5">Subject</label>
              <select value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} className="w-full h-11 px-4 bg-[#0a0a0a] border border-[#262626] text-white text-sm rounded-sm focus:border-[#ccff00] focus:outline-none transition-colors" data-testid="contact-subject">
                <option value="">Select a topic...</option>
                <option value="General Question">General Question</option>
                <option value="Bug Report">Bug Report</option>
                <option value="Feature Request">Feature Request</option>
                <option value="Billing / Subscription">Billing / Subscription</option>
                <option value="Partnership / Business">Partnership / Business</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-[#555] block mb-1.5">Message <span className="text-[#ccff00]">*</span></label>
              <textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} placeholder="How can we help?" required rows={6} className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#262626] text-white text-sm rounded-sm placeholder:text-[#555] focus:border-[#ccff00] focus:outline-none transition-colors resize-none" data-testid="contact-message" />
            </div>
            {status === "error" && <p className="text-sm text-[#ff4d4d]">{"Something went wrong. Please try again or email us directly at support@dynastytrademaker.com."}</p>}
            <button type="submit" disabled={status === "sending" || !form.email || !form.message} className="w-full h-12 bg-[#ccff00] text-black rounded-sm text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-[#b8e600] transition-colors disabled:opacity-50" data-testid="contact-submit">
              {status === "sending" ? <><CircleNotch size={16} className="animate-spin" /> SENDING...</> : <><EnvelopeSimple size={16} weight="bold" /> SEND MESSAGE</>}
            </button>
            <p className="text-[10px] text-[#555] text-center">{"We typically respond within a few hours during business days."}</p>
          </form>
        )}
      </div>
    </div>
  );
}
